import * as R from 'ramda'
import i18n from 'i18next'
import { download } from '../utils/helpers'
import { getQuestionTitle } from '../utils/QuestionHelper'

class WordExport {
    constructor(survey, questions, langcode, appStore) {
        this.appStore = appStore
        this.survey = survey
        this.questions = questions
        this.surveyName = survey.content.title.get(survey.metadata.defaultLocale).text
        this.productName = survey.metadata.productInfo.industrySpecificInfo.productName
        this.welcomeContent = survey.content.welcomeContent.get(survey.metadata.defaultLocale).text
        this.langcode = langcode || 'en_GB'
        this.formattedPages = survey.pages.reduce((pages, p) => {
            if (p.pageType !== 'ST') return pages
            pages.push(
                {
                    ...p,
                    questions: p.questions.map((q) => (
                        {
                            ...q,
                            ...questions.find((question) => (
                                question.pageQuestionId.toLowerCase() === q.id.toLowerCase()
                            )),
                        }
                    )),
                },
            )
            return pages
        }, [])
    }

    getQuestionTitle = ({ question }) => {
        const { survey } = this
        const { defaultLocale } = survey.metadata
        const { initialLocale } = R.or(question.metadata, {})

        const shortTitle = getQuestionTitle(
            {
                content: R.pathOr({}, ['content', 'shortTitle'], question),
                locale: this.langcode,
                defaultLocale,
                initialLocale,
            },
        )

        const longTitle = getQuestionTitle(
            {
                content: R.pathOr({}, ['content', 'longTitle'], question),
                locale: this.langcode,
                defaultLocale,
                initialLocale,
            },
        )

        const instructions = getQuestionTitle(
            {
                content: R.pathOr({}, ['content', 'instructions'], question),
                locale: this.langcode,
                defaultLocale,
                initialLocale,
            },
        )

        return {
            shortTitle: R.pathOr(shortTitle, ['text'], shortTitle),
            longTitle: R.pathOr(longTitle, ['text'], longTitle),
            instructions: R.pathOr(instructions, ['text'], instructions),
        }
    }

    getOptionText = (option, langcode, defaultLocale, initialLocale) => R.pathOr('', ['text'], getQuestionTitle(
        {
            content: R.pathOr({}, ['langs'], option),
            locale: langcode,
            defaultLocale,
            initialLocale,
        },
    ))

    getAxis = ({ question }) => {
        const { questionTypeCode } = question
        if (['RD', 'CH', 'PC', 'TL', 'OD'].includes(questionTypeCode)) {
            return 'single'
        }
        if (['TB', 'RT', 'SG'].includes(questionTypeCode)) {
            return 'double'
        }
        return undefined
    }

    singleAxisTags = ({ question }) => {
        const { optionSet } = question
        const elems = []

        if (optionSet.hasNumericCodes) {
            elems.push('numeric')
        }
        if (optionSet.isExclusive) {
            elems.push('EXCLUSIVE')
        }
        if (optionSet.sortType) {
            elems.push(optionSet.sortType)
        }

        return elems
    }

    doubleAxisTags = ({ question }) => {
        const { isFlipped } = question.settings.frontend
        const { optionSetY, optionSetX } = question
        const rowLabel = isFlipped ? 'Column' : 'Row'
        const colLabel = isFlipped ? 'Row' : 'Column'
        const elems = []
        if (optionSetY.sortType) {
            elems.push(`${rowLabel}: ${optionSetY.sortType}`)
        }
        if (optionSetX.sortType) {
            elems.push(`${colLabel}: ${optionSetX.sortType}`)
        }
        if (optionSetY.isExclusive) {
            elems.push(`${rowLabel}: Exclusive`)
        }
        if (optionSetX.isExclusive) {
            elems.push(`${colLabel}: Exclusive`)
        }
        if (optionSetX.hasNumericCodes) {
            elems.push(`${colLabel}: Numeric`)
        }

        return elems
    }

    singleAxisOptions = ({ question }) => {
        const { survey } = this
        const { optionSet } = question
        const { defaultLocale } = survey.metadata
        const { initialLocale } = R.or(question.metadata, {})
        const { langcode } = this
        return optionSet.options.map((o) => (
            this.getOptionText(o, langcode, defaultLocale, initialLocale)
        ))
    }

    doubleAxisOptions = ({ question }) => {
        const { survey } = this
        const { isFlipped } = question.settings.frontend
        const { optionSetY, optionSetX } = question
        const { defaultLocale } = survey.metadata
        const { initialLocale } = R.or(question.metadata, {})
        const { langcode } = this
        const rowOptions = isFlipped ? optionSetX.options : optionSetY.options
        const colOptions = isFlipped ? optionSetY.options : optionSetX.options

        return [
            '',
            { text: 'Columns: ', underline: true },
            ...colOptions.map((o) => this.getOptionText(o, langcode, defaultLocale, initialLocale)),
            '',
            { text: 'Rows: ', underline: true },
            ...rowOptions.map((o) => this.getOptionText(o, langcode, defaultLocale, initialLocale)),
        ]
    }

    readableOperatorText = (operator) => i18n.t(`wordExport.${operator}`)

    getInheritance = (question) => {
        const { formattedPages } = this
        const { parentId } = question
        if (!parentId) {
            return undefined
        }
        const questionMap = formattedPages.reduce((map, page) => {
            let newMapping = {}
            if (page.questions) {
                newMapping = page.questions.reduce((qMap, q) => ({ ...qMap, [q.id]: q }), {})
            }
            return Object.assign(map, newMapping)
        }, {})
        const parentQuestion = questionMap[parentId]
        const { shortTitle } = this.getQuestionTitle(parentQuestion)
        return `INHERITING Inherits From Question (${shortTitle})`
    }

    getSkipLogic = (question) => {
        const { survey, formattedPages } = this
        const { defaultLocale } = survey.metadata
        const { initialLocale } = R.or(question.question.metadata, {})
        const { langcode } = this
        const questionMap = formattedPages.reduce((map, page) => {
            let newMapping = {}
            if (page.questions) {
                newMapping = page.questions.reduce((qMap, q) => ({ ...qMap, [q.id]: q }), {})
            }
            return Object.assign(map, newMapping)
        }, {})
        const showIfLogics = survey.skipLogic.filter((skiplogic) => (skiplogic.action.kind === 'ShowQuestion' && skiplogic.action.pageQuestionId === question.id))
        const logic = showIfLogics.reduce((str, showIfLogic) => showIfLogic.triggerGroups.reduce((str, triggerGroup) => {
            const { interGroupLogicalOperator: additionalSkipLogicOperatorGroup } = triggerGroup
            const children = R.pathOr([], ['children'], triggerGroup).reduce((str, child) => {
                const { interGroupLogicalOperator: additionalSkipLogicOperatorChild } = child
                let logical = ''
                const triggers = child.triggers.reduce((str, trigger) => {
                    logical = trigger.interTriggerLogicalOperator
                    const { condition } = trigger
                    const { kind } = condition
                    const { interTriggerLogicalOperator } = trigger
                    const logicQuestion = questionMap[condition.pageQuestionId]
                    const logicQuestionQ = R.pathOr({}, ['question'], logicQuestion)
                    const { isFlipped } = logicQuestionQ.settings.frontend
                    const isMulti = ['RT', 'TB', 'SG'].indexOf(logicQuestionQ.questionTypeCode) > -1
                    const isText = ['TA', 'TX'].includes(logicQuestionQ.questionTypeCode)
                    let option
                    let option2

                    if (isMulti) {
                        const set1 = !isFlipped
                            ? logicQuestionQ.optionSetY
                            : logicQuestionQ.optionSetX
                        const set2 = !isFlipped
                            ? logicQuestionQ.optionSetX
                            : logicQuestionQ.optionSetY
                        option = set1.options.find((o) => o.id === condition.set1)
                        if (condition.set2) {
                            option2 = set2.options.find((o) => o.id === condition.set2)
                        }
                    } else if (isText) {
                        if (!str) {
                            const operator = kind === 'QuestionExistsInSurveyResponse'
                                ? 'is answered'
                                : this.readableOperatorText(interTriggerLogicalOperator)
                            str = `Question '${this.getQuestionTitle(logicQuestion).shortTitle}' ${operator}`
                        } else {
                            str += `,'${equals}'`
                        }
                        return str
                    } else {
                        option = logicQuestionQ.optionSet.options
                            .find((o) => o.id === condition.set1)
                    }

                    let equals
                    const option1Text = this.getOptionText(
                        option, langcode, defaultLocale, initialLocale,
                    )

                    const option2Text = this.getOptionText(
                        option2, langcode, defaultLocale, initialLocale,
                    )

                    if (!logicQuestionQ.settings.frontend.isFlipped) {
                        equals = (option ? option1Text : '') + (option2 ? `_${option2Text}` : '')
                    } else {
                        equals = (option2 ? `${option2Text}_` : '') + (option ? option1Text : '')
                    }
                    if (!str) {
                        str = `Question '${this.getQuestionTitle(logicQuestion).shortTitle}' ${this.readableOperatorText(interTriggerLogicalOperator)} ('${equals}'`
                    } else {
                        str += `,'${equals}'`
                    }
                    return str
                }, '')
                if (str) {
                    str += ` ${additionalSkipLogicOperatorChild} `
                }
                str += `${triggers})`
                return str
            }, '')
            if (str) {
                str += ` ${additionalSkipLogicOperatorGroup} `
            }
            str += `(${children})`
            return str
        }, ''), '')
        return logic.length > 0 ? `SHOWIF Hidden unless: ${JSON.stringify(logic)}` : null
    }

    showIfPageLogicText = (skipLogic, questionMap) => {
        const { defaultLocale, langcode } = this
        return skipLogic.reduce((str, showIfLogic) => (
            showIfLogic.triggerGroups.reduce((str, triggerGroup) => {
                const { interGroupLogicalOperator: additionalSkipLogicOperatorGroup } = triggerGroup
                const children = R.pathOr([], ['children'], triggerGroup).reduce((str, child) => {
                    const { interGroupLogicalOperator: additionalSkipLogicOperatorChild } = child
                    let logical = ''
                    const triggers = child.triggers.reduce((str, trigger) => {
                        logical = trigger.interTriggerLogicalOperator
                        const { condition } = trigger
                        const { interTriggerLogicalOperator } = trigger
                        const question = questionMap[condition.pageQuestionId]
                        const questionQ = R.pathOr({}, ['question'], question)
                        const { initialLocale } = questionQ.metadata
                        const { isFlipped } = questionQ.settings.frontend
                        const isMulti = ['RT', 'TB', 'SG'].indexOf(questionQ.questionTypeCode) > -1
                        let option
                        let option2

                        if (isMulti) {
                            const set1 = !isFlipped ? questionQ.optionSetY : questionQ.optionSetX
                            const set2 = !isFlipped ? questionQ.optionSetX : questionQ.optionSetY
                            option = set1.options.find((o) => o.id === condition.set1)
                            if (condition.set2) {
                                option2 = set2.options.find((o) => o.id === condition.set2)
                            }
                        } else if (question.question.optionSet) {
                            option = questionQ.optionSet.options
                                .find((o) => o.id === condition.set1)
                        }

                        let equals
                        const option1Text = this.getOptionText(
                            option, langcode, defaultLocale, initialLocale,
                        )

                        const option2Text = this.getOptionText(
                            option2, langcode, defaultLocale, initialLocale,
                        )

                        if (!questionQ.settings.frontend.isFlipped) {
                            equals = (option ? option1Text : '') + (option2 ? `_${option2Text}` : '')
                        } else {
                            equals = (option2 ? `${option2Text}_` : '') + (option ? option1Text : '')
                        }
                        if (!str) {
                            str = `Question '${this.getQuestionTitle(question).shortTitle}' ${this.readableOperatorText(interTriggerLogicalOperator)} ('${equals}'`
                        } else {
                            str += `,'${equals}'`
                        }

                        return str
                    }, '')
                    if (str) {
                        str += ` ${additionalSkipLogicOperatorChild} `
                    }
                    str += `${triggers})`
                    return str
                }, '')
                if (str) {
                    str += ` ${additionalSkipLogicOperatorGroup} `
                }
                str += `(${children})`
                return str
            }, '')
        ), '')
    }

    getPageSkipLogic = (page) => {
        const { survey, formattedPages } = this
        const { defaultLocale } = survey.metadata
        const { langcode } = this
        const questionMap = formattedPages.reduce((map, p) => {
            let newMapping = {}
            if (p.questions) {
                newMapping = p.questions.reduce((qMap, q) => ({ ...qMap, [q.id]: q }), {})
            }
            return Object.assign(map, newMapping)
        }, {})
        const showIfLogic = survey.skipLogic.filter((skiplogic) => (skiplogic.action.kind === 'ShowPage' && skiplogic.action.pageId === page.id))
        const skipToPageLogics = survey.skipLogic.filter((skiplogic) => (
            skiplogic.action.kind === 'SkipToPage' && skiplogic.action.pageId === page.id
        ))

        const showIfText = this.showIfPageLogicText(showIfLogic, questionMap)
        const logics = skipToPageLogics.map((skipToPageLogic) => {
            const { condition } = skipToPageLogic.trigger || skipToPageLogic.triggerGroups[0].children[0].triggers[0]
            const question = questionMap[condition.pageQuestionId]
            const questionQ = R.pathOr({}, ['question'], question)

            if (!question) {
                return 'IF:INVALID LOGIC'
            }
            const isMulti = ['RT', 'TB', 'SG'].indexOf(questionQ.questionTypeCode) > -1
            const { isFlipped } = questionQ.settings.frontend
            const { initialLocale } = questionQ.metadata
            let option
            let option2
            if (isMulti) {
                const set1 = !isFlipped ? questionQ.optionSetY : questionQ.optionSetX
                const set2 = !isFlipped ? questionQ.optionSetX : questionQ.optionSetY
                option = set1.options.find((o) => o.id === condition.set1)
                if (condition.set2) {
                    option = set2.options.find((o) => o.id === condition.set2)
                }
            } else {
                option = questionQ.optionSet.options.find((o) => o.id === condition.set1)
            }

            const option1Text = this.getOptionText(option, langcode, defaultLocale, initialLocale)

            const option2Text = this.getOptionText(option2, langcode, defaultLocale, initialLocale)

            let equals
            if (!isFlipped) {
                equals = option1Text + (option2 ? `_${option2Text}` : '')
            } else {
                equals = (option2 ? `${option2Text}_` : '') + option1Text
            }
            const targetPage = formattedPages.find((p) => p.id === skipToPageLogic.action.pageId)
            return `IF: Question '${this.getQuestionTitle(question).longTitle}' equals '${equals}' THEN: go to page '${targetPage.title}'`
        })
        const logicElements = []
        if (showIfText.length > 0) {
            logicElements.push(`SHOWIF Hidden unless: ${showIfText}`)
        }
        if (logics.length > 0) {
            logicElements.push(`Page logic: Skip${logics}`)
        }
        return logicElements.length > 0 ? logicElements : null
    }

    createQuestion = (question) => {
        const titles = this.getQuestionTitle(question)
        const inheritenceTxt = this.getInheritance(question)
        const skipLogicTxt = this.getSkipLogic(question)
        const axisCount = this.getAxis(question)

        let questionTags = []
        let options = []
        if (question.question.metadata.isMandatory) {
            questionTags.push('Mandatory')
        }
        if (axisCount) {
            questionTags = questionTags.concat(axisCount === 'single'
                ? this.singleAxisTags(question)
                : this.doubleAxisTags(question))
            options = axisCount === 'single'
                ? this.singleAxisOptions(question)
                : this.doubleAxisOptions(question)
        }
        const questionTypeName = i18n.t(`questionType.${question.question.questionTypeCode}`)

        return {
            questionType: questionTypeName,
            questionTags,
            titles,
            inheritence: inheritenceTxt,
            skipLogic: skipLogicTxt,
            options,
            id: question.pageQuestionId,
        }
    }

    createPage = (page) => {
        const pageSkipLogic = this.getPageSkipLogic(page)

        const children = page.questions.map((q) => this.createQuestion(q))

        return { title: page.title, questions: children, pageSkipLogic }
    }

    requestWordDoc = async () => {
        try {
            const pages = this.formattedPages.map((p) => this.createPage(p))
            const body = {
                welcomeContent: this.welcomeContent,
                surveyName: this.surveyName,
                productName: this.productName,
                id: this.survey.id,
                pages,
            }
            const response = await this.appStore.http.post('/wordExport', JSON.stringify({ survey: body }))
            const fileUrl = response.wordExport
            const fileName = fileUrl.match(/(?<=wordExports\/)(.*?)(?=\?)/)[0]
            download(fileUrl, fileName)
        } catch (e) {
            console.log(e)
            this.appStore.handleError({ body: 'Error exporting survey to word', level: 'error' })
        }
    }
}

export default WordExport
